import React, {
  useState,
  createContext,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { v4 as uuidv4 } from 'uuid';

import Header from '../header';
import Navigation from '../navigation';
import Alert, {
  AlertTypes as OriginalAlertTypes,
} from '../../../components/alert';

import { useNavigate, useLocation } from 'react-router-dom';
import qs from 'qs';
import { AlertProps } from '../../../components/alert/Alert';
import FullPageLoadingSpinner from '../fullPageLoadingSpinner';
import { setWindowTitle } from '../../../configTdm';
import { getAlerts, getSpinnerShow} from '../../../fsConfig';
import { useSelector } from 'react-redux';

type AlertTypes = OriginalAlertTypes | 'conducive';


type PagePropsT = {
  Content: any;
  csrf?: string;
  HelpPageComponent?: React.ComponentType;
  path: string;
  title: string;
  helperFlag?: boolean;
  supportFlag?: boolean;
};

export type PushAlert = (
  type: AlertTypes,
  message: string,
  timeoutInMilliSeconds?: number,
) => void;

export const PageContext = createContext<any>(null);


const useQuery = () => {
  const { search } = useLocation();
  return useMemo(() => qs.parse(search, { ignoreQueryPrefix: true }), [search]);
};

const Page: React.FC<PagePropsT> = ({
  Content,
  csrf,
  HelpPageComponent,
  path,
  title = 'Trip Center',
  helperFlag,
  supportFlag,
}) => {
  const navigate = useNavigate();
  const spinnerShow = useSelector(getSpinnerShow);
  const propAlerts = useSelector(getAlerts);
  const [alerts, updateAlerts] = useState<{ [key: string]: AlertProps }>({});

  useEffect(() => {
    if (propAlerts.length) {
      const alertId = uuidv4();
      const newAlert: AlertProps = {type: propAlerts[0].type, message: propAlerts[0].msg, alertId};
      updateAlerts( () => {
        return {
          [alertId]: newAlert,
        };
      }
      );
    } else {
      updateAlerts({});
    }
  }, [propAlerts]);


  const location = useLocation();
  const isTravelerPath = location.pathname.startsWith('/traveler');

  const deleteAlert = useCallback(
    (alertId: string) => {
      if (alertId && alerts[alertId]) {
        updateAlerts(previousAlertsState => {
          const newAlerts = { ...previousAlertsState };
          delete newAlerts[alertId];
          return newAlerts;
        });
      } else {
        console.warn(
          `Attempted to delete nonexistant alert or alertId: ${alertId}`,
        );
      }
    },
    [alerts],
  );

  // function to make available through the page context to allow publishing of info, success, warning, and danger alerts in the header
  const pushAlert: PushAlert = useCallback(
    (type, message, timeoutInMilliSeconds = 10000) => {
      const alertId = uuidv4();
      updateAlerts(prevAlertsState => {
        const successiveHideDelayMs = 500;
        const timeoutDelay =
          ((typeof prevAlertsState === 'object' &&
            Object.keys(prevAlertsState).length) ||
            0) * successiveHideDelayMs;
        if (type === 'conducive') {
          return {
            ...prevAlertsState,
            [alertId]: {
              type: 'danger',
              message:
                'This page was not intended to function properly for conducive ( accountId: 345 ) users',
              alertId,
              timeoutInMilliSeconds: 300_000,
            },
          };
        } else {
          return {
            ...prevAlertsState,
            [alertId]: {
              type,
              message,
              alertId,
              timeoutInMilliSeconds: timeoutInMilliSeconds + timeoutDelay,
            },
          };
        }
      });
    },
    [],
  );

  const pageContextVal = {
    csrf, // this is included for forms that submit themselves instead of using js to make api calls
    HelpPageComponent,
    navigate,
    path,
    pushAlert,
    query: useQuery(),
    title,
    helperFlag,
    supportFlag
  };

  setWindowTitle(title);

  return (
    <>
      {spinnerShow && <FullPageLoadingSpinner></FullPageLoadingSpinner>}
      <PageContext.Provider value={pageContextVal}>
        <div className="container-fluid">
          {!isTravelerPath && (
            <>
              <Header />
              {Object.values(alerts).map((alert: AlertProps, index: number) => (
                <Alert
                  type={alert.type}
                  timeoutInMilliSeconds={alert.timeoutInMilliSeconds}
                  message={alert.message}
                  key={index.toString()}
                  onHide={() => deleteAlert(alert.alertId)}
                />
              ))}
              <Navigation />
            </>
          )}
          <Content />
        </div>
      </PageContext.Provider>
    </>
  );
};

export default Page;

